import { AfterViewInit, Component, inject, input, OnChanges, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { PanModalComponent } from '../components/pan-modal/pan-modal.component';
import { UserSearchService } from '../service/user-search.service';
import { ShareCasesRequest, UserSearchResult, UserSearchType } from '../types';

@Component({
  selector: 'app-share-email-modal',
  templateUrl: './share-email-modal.component.html',
  styleUrl: './share-email-modal.component.scss',
  standalone: false,
})
export class ShareEmailModalComponent implements AfterViewInit, OnChanges {
  public userSearch = inject(UserSearchService);
  private auth = inject(AuthService);

  public modalTitle = input<string>('');
  public searchType = input<UserSearchType>();
  // only required if using 'locations' searchType
  public locationIds = input<number[] | undefined>();
  public isSharing = input<boolean>(false);
  public disabled = input<boolean>(false);

  public searchInput$ = new BehaviorSubject<string>('');
  public usersToShare = signal<UserSearchResult[]>([]);
  public emailError = signal<string>('');

  public isGuestMode = signal<boolean>(false);
  public shareMessage = signal<string>('');
  public shareIncludeSelf = signal<boolean>(false);

  public share = output<Partial<ShareCasesRequest>>();

  private modal = viewChild<PanModalComponent>('modal');

  constructor() {
    this.searchInput$.pipe(debounceTime(500), takeUntilDestroyed()).subscribe((q) => this.triggerSearch(q));
  }

  triggerSearch(query: string) {
    switch (this.searchType()) {
      case 'branches':
        return this.userSearch.search(query, { by: 'branches' });
      case 'locations':
        return this.userSearch.search(query, { by: 'locations', locationIds: this.locationIds() });
    }
  }

  ngOnChanges() {
    this.triggerSearchIfAuthenticated(this.searchInput$.getValue());
  }

  ngAfterViewInit() {
    this.triggerSearchIfAuthenticated('');
  }

  // This modal is always instantiated (panRole only hides it via CSS), including for anonymous
  // viewers of an opted-out shared report, so the eager search must not run without a session -
  // otherwise Auth0's silent token refresh fails and force-redirects the anonymous viewer to login.
  private triggerSearchIfAuthenticated(query: string) {
    this.auth.isAuthenticated$.pipe(take(1)).subscribe((isAuthenticated) => {
      if (isAuthenticated) this.triggerSearch(query);
    });
  }

  validateUserToShare() {
    const users = this.usersToShare();
    this.emailError.set('');
    for (const user of users) {
      if (!this.isValidEmail(user.email)) {
        this.emailError.set(`Invalid email format: ${user.email}`);
        return;
      }
    }
  }

  removeUser(user: UserSearchResult) {
    this.usersToShare.set(this.usersToShare().filter((u) => u !== user));
  }

  hide() {
    this.usersToShare.set([]);
    this.isGuestMode.set(false);
    this.shareMessage.set('');
    this.shareIncludeSelf.set(false);
    this.emailError.set('');
  }

  private sharedShareDetails(): Partial<ShareCasesRequest> {
    return {
      cc_sender: this.shareIncludeSelf(),
      message: this.shareMessage(),
    };
  }

  onShare() {
    const users = this.usersToShare();
    const emails = users.map((u) => u.email);
    this.validateUserToShare();

    const uniqueUsers = users.filter((u, idx) => users.findIndex((v) => v.email === u.email) === idx);
    const validUsers = uniqueUsers.filter((u) => this.isValidEmail(u.email));
    this.usersToShare.set(validUsers);

    const requiresConfirm = users.some((user) => user.isGuest || (!user.id && user.email));

    const emitEmails = () => {
      this.share.emit({
        ...this.sharedShareDetails(),
        emails,
      });

      this.modal().hide();
    };

    if (!requiresConfirm) {
      emitEmails();
    } else {
      Swal.fire({
        title: 'Confirm Share',
        text: 'Some emails are not on the guest user list. Confirming will grant these users access to this case report.',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
      }).then((result) => {
        if (result.isConfirmed) {
          emitEmails();
        }
      });
    }
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  public shareToAllGuestUsers() {
    this.share.emit({
      ...this.sharedShareDetails(),
      to_guests: true,
    });
  }
}
